import { ApiClientInstance, ServiceHelper } from "utils/helpers";
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
import { store } from "store";
import { ServiceConstants } from "utils/constants";
import { hasRelationFlag } from "utils/helpers/relationFlagHelper";

/**
 * @deprecated This service is deprecated. Use useCompanyService hook instead.
 *
 * Migration guide:
 * 1. Replace CompanyService calls with useCompanyService hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle company state through Redux hooks
 *
 * @see useCompanyService - The replacement hook for company functionality
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 */
const CompanyService = {
  getCompanyData: (companyId: number): Promise<ApiModels.Company> => {
    return ApiClientInstance(AuthEnums.AuthType.User)
      .CompanyClient.GetById(companyId)
      .then((res) => res.data);
  },

  getCompanies: () => {
    return ServiceHelper.getAllPages((page) =>
      ApiClientInstance(AuthEnums.AuthType.User).CompanyClient.GetByFilter({
        ...ServiceConstants.DefaultFilter,
        orderByProperty: "Name",
        orderByDescending: false,
        page: page,
      })
    );
  },

  /**
   * @deprecated This function is deprecated. Use useCheckRelations hook instead.
   *
   * @see useCheckRelations - The replacement hook for company relations check
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const hasRelation = CompanyService.hasRelation(relationType);
   *
   * @example
   * // New way (recommended):
   * const { hasRelation } = useCheckRelations();
   * const hasRel = hasRelation(relationType);
   */
  hasRelation: (personCompanyRelationType: AuthEnums.PersonCompanyRelationType, ignoreAdmin: boolean = false) => {
    const relationFlags = store.getState().auth.selectedCompany.extendedRelationFlags;

    const adminOverrides =
      hasRelationFlag(relationFlags, AuthEnums.PersonCompanyRelationType.Admin) &&
      !ignoreAdmin &&
      personCompanyRelationType !== AuthEnums.PersonCompanyRelationType.Auditor;
    return adminOverrides || hasRelationFlag(relationFlags, personCompanyRelationType);
  },

  /**
   * @deprecated This function is deprecated. Use useCheckRelations hook instead.
   *
   * @see useCheckRelations - The replacement hook for company relations check
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const hasRelations = CompanyService.hasRelations(relationType);
   *
   * @example
   * // New way (recommended):
   * const { hasRelations } = useCheckRelations();
   * const hasRel = hasRelations(relationType);
   */
  hasRelations: (personCompanyRelationTypes: AuthEnums.PersonCompanyRelationType[], ignoreAdmin: boolean = false) => {
    const relationFlags = store.getState().auth.selectedCompany.extendedRelationFlags;

    const adminOverrides =
      hasRelationFlag(relationFlags, AuthEnums.PersonCompanyRelationType.Admin) &&
      !ignoreAdmin &&
      personCompanyRelationTypes.every((x) => x !== AuthEnums.PersonCompanyRelationType.Auditor);
    return (
      adminOverrides || personCompanyRelationTypes.some((x) => hasRelationFlag(relationFlags, x))
    );
  },

  SearchPersons: (
    searchStr: string,
    excludeSelf?: boolean,
    excludeSalaryInactive?: boolean,
    excludeNotInBusinessUnitWithSalaryRights?: boolean
  ) => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .CompanyClient.SearchPersons({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        excludeSelf: excludeSelf ?? true,
        excludeSalaryInactive: excludeSalaryInactive ?? false,
        excludeNotInBusinessUnitWithSalaryRights: excludeNotInBusinessUnitWithSalaryRights ?? false,
      })
      .then((res) => (res.data.items ? res.data.items : []));
  },

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useTimeReportingPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isEnabled = CompanyService.isTimeReportingEnabled();
   *
   * @example
   * // New way (recommended):
   * function useTimeReportingPermissions() {
   *   const company = useSelector((state: AppState) => state.auth.selectedCompany);
   *
   *   const isTimeReportingEnabled = () => {
   *     return company.isTimeReportingEnabled;
   *   };
   *
   *   return { isTimeReportingEnabled };
   * }
   *
   * // In component:
   * const { isTimeReportingEnabled } = useTimeReportingPermissions();
   * const isEnabled = isTimeReportingEnabled();
   */
  isTimeReportingEnabled: () => {
    const state = store.getState();
    return state.auth.selectedCompany.isTimeReportingEnabled;
  },

  /**
   * @deprecated This function is deprecated. Use useSelector for Redux state access.
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isDebtCollectionEnabled = CompanyService.isDebtCollectionEnabled();
   *
   * @example
   * // New way (recommended):
   * const isDebtCollectionEnabled = useSelector((state: AppState) => state.auth.selectedCompany.isDebtCollectionEnabled);
   */
  isDebtCollectionEnabled: () => {
    const state = store.getState();
    return state.auth.selectedCompany.isDebtCollectionEnabled;
  },

  /**
   * @deprecated This function is deprecated. Use useSelector for Redux state access.
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const hasSavedFinancialStatements = CompanyService.hasSavedFinancialStatements();
   *
   * @example
   * // New way (recommended):
   * const hasSavedFinancialStatements = useSelector((state: AppState) => state.auth.selectedCompany.hasSavedFinancialStatements);
   */
  hasSavedFinancialStatements: () => {
    const state = store.getState();
    return state.auth.selectedCompany.hasSavedFinancialStatements;
  },
};

export default CompanyService;
